import React from "react";
import { ArrowUpRight, Check, CornerDownRight, Mic, Plane, Play, Sparkles, Volume2 } from "lucide-react";
import imageSize from "./image-size";
import { KineticArt, HapticArt, DrawTuneArt } from "./product-art-additions";
import "../styles/project-art.css";

function ViralVideoArt() {
  return (
    <div className="ugc-scene">
      <div className="art-eyebrow"><span className="art-status-dot" /> Select a video presenter.</div>
      <div className="ugc-previews">
        {["Beauty", "Style", "Tech"].map((category, index) => (
          <div className={`ugc-preview ugc-preview--${index}`} key={category}>
            <img src="/projects/viral-ugc-stills-v1.webp" {...imageSize("/projects/viral-ugc-stills-v1.webp")} alt="" loading="lazy" />
            <span className="ugc-category">{category}</span>
            <span className="ugc-play"><Play fill="currentColor" /></span>
            <span className="ugc-duration">0:{[15, 20, 10][index]}</span>
          </div>
        ))}
      </div>
      <div className="ugc-prompt">
        <Sparkles />
        <span><small>Start a video</small><strong>Describe the story.</strong></span>
        <span className="ugc-prompt-send"><ArrowUpRight /></span>
      </div>
    </div>
  );
}

function JotArt() {
  return (
    <div className="jot-scene">
      <div className="jot-question"><Mic /><span>Where is my passport?</span></div>
      <div className="jot-answer">
        <span className="jot-found"><Check /> Saved location</span>
        <strong>Gray drawer,<br />bedroom.</strong>
        <span className="jot-location"><CornerDownRight /> Passport · saved by you</span>
        <span className="jot-read"><Volume2 /> Read aloud</span>
      </div>
      <div className="jot-listen"><span><Mic /></span><small>Record the location.<br />Find it later.</small></div>
    </div>
  );
}

const clips = [
  { caption: <>the <em>one</em> mistake</>, duration: "0:52" },
  { caption: <>nobody <em>tells you</em></>, duration: "0:41" },
  { caption: <>do this <em>first</em></>, duration: "0:38" },
];

function ReelArt() {
  return (
    <div className="reel-scene">
      <div className="reel-ready"><Check /> 3 shorts, ready to share</div>
      <div className="reel-clips">
        {clips.map((clip, index) => (
          <div className="reel-clip-column" key={clip.duration}>
            <div className={`reel-clip reel-clip--${index}`}>
              <img src="/projects/reel-creators-v1.webp" {...imageSize("/projects/reel-creators-v1.webp")} alt="" loading="lazy" />
              <span className="reel-clip-number">0{index + 1}</span>
              <span className="reel-clip-caption">{clip.caption}</span>
              <span className="reel-clip-duration"><Play fill="currentColor" /> {clip.duration}</span>
            </div>
            <span className="reel-connector" />
          </div>
        ))}
      </div>
      <div className="reel-timeline">
        <div className="reel-file"><span><Play fill="currentColor" /> episode-142.mp4</span><span>24:18</span></div>
        <div className="reel-track">
          <div className="reel-wave">{Array.from({ length: 66 }, (_, index) => <i key={index} style={{ height: `${12 + ((index * 17 + index * index) % 31)}px` }} />)}</div>
          {[0, 1, 2].map(index => <span key={index} className={`reel-selection reel-selection--${index}`}><b>0{index + 1}</b></span>)}
          <span className="reel-playhead" />
        </div>
        <div className="reel-ruler"><span>00:00</span><span>08:00</span><span>16:00</span><span>24:18</span></div>
      </div>
    </div>
  );
}

function TellsArt() {
  return <div className="tells-illustrated"><img src="/projects/tells-well-conversation.webp" width="1400" height="788" alt="" loading="lazy" /><span>Practice a story before the conversation.</span></div>;
}

function OfflineArt() {
  return <div className="offline-scene"><div className="offline-flight"><span /><Plane strokeWidth={1.4} /></div><div className="offline-thought"><span>✳</span><strong>Ask a question.</strong></div><span className="offline-caption">On your phone. Without the internet.</span></div>;
}

function VoiceArt() {
  return <div className="voice-scene"><span className="voice-caption">Your personal AI agents.</span><div className="voice-orbits"><div className="voice-avatar voice-avatar--water"><i /><i /><i /><i /></div><div className="voice-avatar voice-avatar--forest"><i /><i /><i /></div><div className="voice-avatar voice-avatar--coral"><i /><i /><i /></div></div><strong>Discuss your ideas.</strong><div className="voice-art-call"><Mic /><span>Use voice or text in the same conversation.</span></div></div>;
}

function WitArt() {
  return <div className="wit-scene"><img src="/projects/fit-your-wit-park.webp" width="1400" height="583" alt="" loading="lazy" /><div><span>One prompt. Your response.</span><strong>Practice your next reply.</strong></div></div>;
}

const artwork = {
  "viral-video-maker": ViralVideoArt,
  "jot-the-spot": JotArt,
  "video-to-reel": ReelArt,
  "tells-well": TellsArt,
  "offline-ai-chat": OfflineArt,
  "my-voice-agents": VoiceArt,
  "fit-your-wit": WitArt,
  "kinetic": KineticArt,
  "haptic-asmr": HapticArt,
  "draw-a-tune": DrawTuneArt,
};

export default function ProjectArt({ project }) {
  const Artwork = artwork[project];
  return <div className={`project-art project-art--${project}`} aria-hidden="true"><Artwork /></div>;
}
